import type {
  ApiError,
  DashboardPayload,
  PublicUser,
  Report,
  ReportInput,
  Role,
  SessionPayload,
  UserStatus
} from "./types";
import { getTelegramInitData } from "./telegram";

const API_BASE = import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, "") ?? "";

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly apiError: ApiError
  ) {
    super(apiError.message);
  }
}

export async function getSession(): Promise<SessionPayload> {
  return request<SessionPayload>("/auth/session", { method: "POST" });
}

export async function getTodayReport(): Promise<{ report: Report | null; today: string; deadlineAt: string }> {
  return request("/reports/today");
}

export async function saveTodayReport(input: ReportInput, hasReport: boolean): Promise<{ report: Report }> {
  return request("/reports/today", {
    method: hasReport ? "PUT" : "POST",
    body: JSON.stringify(input)
  });
}

export async function getDashboard(date: string): Promise<DashboardPayload> {
  return request(`/dashboard/daily?date=${encodeURIComponent(date)}`);
}

export async function getRules(): Promise<{ rules: string }> {
  return request("/settings/rules");
}

export async function updateRules(rules: string): Promise<{ rules: string }> {
  return request("/settings/rules", {
    method: "PUT",
    body: JSON.stringify({ rules })
  });
}

export async function listUsers(): Promise<{ users: PublicUser[] }> {
  return request("/admin/users");
}

export async function createUser(input: {
  telegramId: string;
  fullName: string;
  username: string | null;
  role: Role;
  status: UserStatus;
}): Promise<{ user: PublicUser }> {
  return request("/admin/users", {
    method: "POST",
    body: JSON.stringify(input)
  });
}

export async function updateUser(
  id: string,
  input: Partial<Pick<PublicUser, "fullName" | "username" | "role" | "status">>
): Promise<{ user: PublicUser }> {
  return request(`/admin/users/${id}`, {
    method: "PATCH",
    body: JSON.stringify(input)
  });
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      "content-type": "application/json",
      ...authHeaders(),
      ...init.headers
    }
  });
  const payload = (await response.json().catch(() => null)) as
    | { data?: T; error?: ApiError }
    | null;

  if (!response.ok) {
    throw new ApiRequestError(response.status, payload?.error ?? { code: "request_failed", message: "So'rov bajarilmadi" });
  }

  return payload?.data as T;
}

function authHeaders(): Record<string, string> {
  const initData = getTelegramInitData();
  if (initData) return { "x-telegram-init-data": initData };

  const devTelegramId = import.meta.env.VITE_DEV_TELEGRAM_ID;
  if (import.meta.env.DEV && devTelegramId) {
    return { "x-dev-telegram-id": devTelegramId };
  }

  return {};
}
